import type { RichTextCommand } from '@firstbite/ui'

/**
 * Shared rich-text wiring for the fb-ui `ActivityComposer` — bridges its
 * toolbar `onCommand` to the browser's `execCommand` on the composer's
 * `contentEditable` surface. `execCommand` is deprecated but dependency-free and
 * is exactly what fb-ui's RichTextEditor story uses as the reference wiring.
 *
 * Extracted from the Contact detail page so the Deal detail page (and any future
 * entity page) reuse one implementation.
 */

/** The block-level format tag under the cursor (e.g. `"blockquote"`, `"div"`),
 *  lowercased; `""` when unavailable. */
export function currentFormatBlock(): string {
  try {
    return String(document.queryCommandValue('formatBlock')).toLowerCase()
  } catch {
    return ''
  }
}

/**
 * Toggle a `formatBlock` wrapper (blockquote / code). `execCommand('formatBlock',
 * blockquote)` only ever *applies* the wrapper — calling it again while already
 * inside one is a no-op, so the button can't turn off. Toggling means: if the
 * cursor is already in `tag`, format back to a plain `div` (the contentEditable
 * default block); otherwise apply `tag`.
 */
export function toggleFormatBlock(tag: 'blockquote' | 'pre'): void {
  const next = currentFormatBlock() === tag ? 'div' : tag
  document.execCommand('formatBlock', false, next)
}

/** Toolbar command → `execCommand` action. The inline/list commands toggle
 *  natively; only the block wrappers (blockquote/code) need the toggle helper. */
export const RICH_TEXT_EXEC: Partial<Record<RichTextCommand, () => void>> = {
  bold: () => document.execCommand('bold'),
  italic: () => document.execCommand('italic'),
  underline: () => document.execCommand('underline'),
  strikethrough: () => document.execCommand('strikeThrough'),
  bulletList: () => document.execCommand('insertUnorderedList'),
  orderedList: () => document.execCommand('insertOrderedList'),
  blockquote: () => toggleFormatBlock('blockquote'),
  code: () => toggleFormatBlock('pre'),
}

/** Reflect the toolbar's pressed state from the current selection, so an active
 *  format stays highlighted until toggled off — not just while hovered. */
export function activeRichTextCommands(): RichTextCommand[] {
  const active: RichTextCommand[] = []
  const state = (c: string) => {
    try {
      return document.queryCommandState(c)
    } catch {
      return false
    }
  }
  if (state('bold')) active.push('bold')
  if (state('italic')) active.push('italic')
  if (state('underline')) active.push('underline')
  if (state('strikeThrough')) active.push('strikethrough')
  if (state('insertUnorderedList')) active.push('bulletList')
  if (state('insertOrderedList')) active.push('orderedList')
  const block = currentFormatBlock()
  if (block === 'blockquote') active.push('blockquote')
  if (block === 'pre') active.push('code')
  return active
}

/** Minimal HTML escape for values interpolated into an inserted link. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
